import { motion } from "framer-motion";
import { Database, Cloud, ShieldCheck } from "lucide-react";

const features = [
  {
    icon: Database,
    title: "Metadata-Driven",
    desc: "Configuration over code. Build and evolve applications through structured metadata for ultra-fast deployment.",
  },
  {
    icon: Cloud,
    title: "Cloud-Native",
    desc: "Architected for elastic scale and minimal infrastructure overhead, keeping costs predictable.",
  },
  {
    icon: ShieldCheck,
    title: "Zero-Trust Security",
    desc: "Role-Based Access Control and isolated data boundaries are baked into every layer.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.12 },
  }),
};

export const About = () => {
  return (
    <section
      id="about"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:sticky lg:top-28"
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]">
                Our DNA
              </span>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                The AI-First Approach
              </h2>
              <p className="mt-6 text-base text-[#94A3B8] leading-relaxed">
                At Rishmathi Tech Ventures Private Limited, we believe the next
                generation of software shouldn't just store data—it should
                understand it. Incorporated in 2026, we are a dedicated product
                studio designed to replace legacy complexity with elegant,
                intelligent automation.
              </p>
              <p className="mt-4 text-base text-[#94A3B8] leading-relaxed">
                By blending advanced AI capabilities with robust software
                architecture, we deliver solutions that drastically reduce
                development time and overhead for organizations worldwide.
              </p>
            </motion.div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className={`group bg-[#0F172A]/50 border border-white/5 rounded-2xl p-8 hover:border-[#6366F1]/40 transition-colors backdrop-blur-sm ${
                  i === 0 ? "sm:col-span-2" : ""
                }`}
                data-testid={`about-feature-${i}`}
              >
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#6366F1]/20 to-[#06B6D4]/20 border border-white/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <f.icon className="text-[#06B6D4]" size={24} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white">
                  {f.title}
                </h3>
                <p className="mt-3 text-[#94A3B8] leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
